import type { EjsBlockData } from '../../editor/editorjs/@types';
import { ReactComponent as IconImage } from '../../icons/icon-image.inline.svg';
import { ReactComponent as IconLink } from '../../icons/icon-link.svg';
import { ReactComponent as IconQuote } from '../../icons/icon-quote.svg';
import { ReactComponent as IconUndefined } from '../../icons/icon-undefined.svg';
import { ReactComponent as IconVideo } from '../../icons/icon-video.svg';
import type { ArrayGroupedBy } from '../../types/utils';
import type { MessageParsedEmbeds } from '../ThreadMessage/threadTypes';

const embedLikeBlocks = ['linkTool', 'quote', 'image', 'embed'] as const;
const isEmbedLikeBlock = (
  block: EjsBlockData,
): block is Extract<EjsBlockData, { type: (typeof embedLikeBlocks)[number] }> =>
  embedLikeBlocks.includes(block.type as (typeof embedLikeBlocks)[number]);

type EmbedsOverviewProps = {
  messageEmbeds: MessageParsedEmbeds;
};

const getEmbedIcon = (embedType: string) => {
  switch (embedType) {
    case 'linkTool':
      return IconLink;
    case 'quote':
      return IconQuote;
    case 'image':
      return IconImage;
    case 'embed':
      return IconVideo;
    default:
      return IconUndefined;
  }
};

export const EmbedsOverview = ({ messageEmbeds }: EmbedsOverviewProps) => {
  const embededBlocks = messageEmbeds.blocks!.filter(isEmbedLikeBlock);
  const sortedEmbeds: ArrayGroupedBy<typeof embededBlocks, 'type'> = embededBlocks.reduce(
    (acc, block) => {
      if (!acc[block.type]) acc[block.type] = [];
      acc[block.type].push(block);
      return acc;
    },
    {},
  );

  return (
    <div className="mt-2 flex justify-center items-center gap-4 text-center">
      {sortedEmbeds &&
        Object.entries(sortedEmbeds).map(([type, blocks], index) => (
          <EmbedOverviewItem key={index} type={type} blocks={blocks} />
        ))}
    </div>
  );
};

const EmbedOverviewItem = ({ type, blocks }: { type: string; blocks: EjsBlockData[] }) => {
  const EmbedIcon = getEmbedIcon(type);
  return (
    <div className="EmbedOverviewItem flex border border-gray-250 rounded-md overflow-hidden">
      <div className="icon-container flex items-center bg-primary text-white px-2">
        <EmbedIcon className="w-4 h-4" />
      </div>
      <div className="counter-container px-2">{blocks.length}</div>
    </div>
  );
};
